import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

interface LoopFormData {
	name: string
	description: string | null
	textQuestionLabel: string | null
	ratingQuestionLabel: string | null
	textQuestionEnabled: boolean
	ratingQuestionEnabled: boolean
	ratingScale: number | null
	scheduleType: 'one-time' | 'recurring'
	scheduleFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | null
	scheduleStartDate: string | null
	scheduleEndDate: string | null
	emailTemplateId: string | null
	populationId: string | null
}

export interface LoopData {
	id: string
	name: string
	description: string | null
	status: 'draft' | 'ready' | 'active' | 'paused' | 'completed'
	organizationId: string
	textQuestionLabel: string | null
	ratingQuestionLabel: string | null
	textQuestionEnabled: boolean
	ratingQuestionEnabled: boolean
	ratingScale: number | null
	scheduleType: 'one-time' | 'recurring'
	scheduleFrequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | null
	scheduleStartDate: string | null
	scheduleEndDate: string | null
	emailTemplateId: string | null
	populationId: string | null
}

interface LoopState {
	loop: LoopData | null
	isLoading: boolean
	error: string | null

	setLoop: (loop: LoopData) => void
	resetLoop: () => void
	saveLoop: () => LoopFormData
	updateLoop: (data: Partial<LoopFormData>) => void
	setError: (error: string | null) => void

	useCreateLoop: () => {
		createLoop: (data: LoopFormData) => Promise<void>
		isLoading: boolean
	}
	useEditLoop: (loopId?: string) => {
		editLoop: (data: Partial<LoopFormData>) => Promise<void>
		isLoading: boolean
	}
	useMarkReadyLoop: (loopId: string) => {
		markReadyLoop: () => Promise<void>
		isLoading: boolean
	}
}

export const useLoopStore = create<LoopState>()(
	devtools(
		immer((set, get) => ({
			loop: null,
			isLoading: false,
			error: null,

			setLoop: (loop: LoopData) =>
				set((state) => {
					state.loop = loop
				}),

			resetLoop: () =>
				set((state) => {
					state.loop = null
					state.error = null
				}),

			updateLoop: (data: Partial<LoopFormData>) =>
				set((state) => {
					if (state.loop) {
						state.loop = {
							...state.loop,
							...data,
						}
					} else {
						state.loop = {
							id: '',
							name: '',
							description: null,
							status: 'draft',
							organizationId: '',
							textQuestionLabel: null,
							ratingQuestionLabel: null,
							textQuestionEnabled: true,
							ratingQuestionEnabled: true,
							ratingScale: 5,
							scheduleType: 'one-time',
							scheduleFrequency: null,
							scheduleStartDate: null,
							scheduleEndDate: null,
							emailTemplateId: null,
							populationId: null,
							...data,
						}
					}
				}),

			setError: (error: string | null) =>
				set((state) => {
					state.error = error
				}),

			saveLoop: () => {
				const state = get()
				if (!state.loop) throw new Error('No loop data to save')
				return {
					name: state.loop.name,
					description: state.loop.description,
					textQuestionLabel: state.loop.textQuestionLabel,
					ratingQuestionLabel: state.loop.ratingQuestionLabel,
					textQuestionEnabled: state.loop.textQuestionEnabled,
					ratingQuestionEnabled: state.loop.ratingQuestionEnabled,
					ratingScale: state.loop.ratingScale,
					scheduleType: state.loop.scheduleType,
					scheduleFrequency: state.loop.scheduleFrequency,
					scheduleStartDate: state.loop.scheduleStartDate,
					scheduleEndDate: state.loop.scheduleEndDate,
					emailTemplateId: state.loop.emailTemplateId,
					populationId: state.loop.populationId,
				}
			},

			useCreateLoop: () => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const navigate = useNavigate()
				const loop = get().loop

				const createLoop = useMutation({
					...trpc.admin.loops.create.mutationOptions(),
					onSuccess: (data) => {
						toast.showToast('Loop created successfully')
						if (loop?.organizationId) {
							navigate(`/organizations/${loop.organizationId}/loops/${data.id}/general`)
						}
					},
					onError: () => {
						toast.showToast('Error creating loop', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['loops']] })
					},
				})

				return {
					createLoop: async (data: LoopFormData) => {
						if (!loop?.organizationId) return
						await createLoop.mutateAsync({
							organizationId: loop.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							textQuestionLabel: data.textQuestionLabel ?? undefined,
							ratingQuestionLabel: data.ratingQuestionLabel ?? undefined,
							textQuestionEnabled: data.textQuestionEnabled,
							ratingQuestionEnabled: data.ratingQuestionEnabled,
							ratingScale: (data.ratingScale === 5 || data.ratingScale === 10 ? data.ratingScale : undefined) as
								| 5
								| 10
								| undefined,
						})
					},
					isLoading: createLoop.isPending,
				}
			},

			useEditLoop: (loopId?: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const loop = get().loop

				const editMutation = useMutation({
					...trpc.admin.loops.update.mutationOptions(),
					onSuccess: () => {
						toast.showToast('Loop updated successfully')
					},
					onError: () => {
						toast.showToast('Error updating loop', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.loops.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.loops.getById.queryKey({ id: loopId }),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					editLoop: async (data: Partial<LoopFormData>) => {
						if (!loop?.organizationId || !loopId) return
						await editMutation.mutateAsync({
							id: loopId,
							organizationId: loop.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							textQuestionLabel: data.textQuestionLabel ?? undefined,
							ratingQuestionLabel: data.ratingQuestionLabel ?? undefined,
							textQuestionEnabled: data.textQuestionEnabled,
							ratingQuestionEnabled: data.ratingQuestionEnabled,
							ratingScale: (data.ratingScale === 5 || data.ratingScale === 10 ? data.ratingScale : undefined) as
								| 5
								| 10
								| undefined,
							scheduleType: data.scheduleType,
							scheduleFrequency: data.scheduleFrequency ?? undefined,
							scheduleStartDate: data.scheduleStartDate ?? undefined,
							scheduleEndDate: data.scheduleEndDate ?? undefined,
							emailTemplateId: data.emailTemplateId ?? undefined,
							populationId: data.populationId ?? undefined,
						})
					},
					isLoading: editMutation.isPending,
				}
			},

			useMarkReadyLoop: (loopId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const loop = get().loop

				const markReadyMutation = useMutation({
					...trpc.admin.loops.markReady.mutationOptions(),
					onSuccess: () => {
						toast.showToast('Loop marked as ready')
					},
					onError: () => {
						toast.showToast('Error marking loop as ready', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.loops.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.loops.getById.queryKey({ id: loopId }),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					markReadyLoop: async () => {
						if (!loop?.organizationId) return
						await markReadyMutation.mutateAsync({
							id: loopId,
							organizationId: loop.organizationId,
						})
					},
					isLoading: markReadyMutation.isPending,
				}
			},
		})),
		{ name: 'loop-store' },
	),
)
